import { useState } from 'react';
import {
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Tag,
  Tooltip,
  Typography,
  type TableProps,
} from 'antd';
import {
  EditOutlined,
  KeyOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, ResponsiveList } from '@mall/shared';
import {
  activateTenant,
  createTenant,
  listTenants,
  resetAdminPassword,
  suspendTenant,
  updateTenant,
} from './api';
import {
  TENANT_STATUS_COLOR,
  TENANT_STATUS_LABEL,
  type AdminAccountCredentials,
  type CreateTenantRequest,
  type TenantStatus,
  type TenantView,
  type UpdateTenantRequest,
} from './types';
import { useToast } from '../../app/ToastProvider';
import { CredentialsModal } from './CredentialsModal';
import { ResetPasswordModal, type ResetPasswordSubmitPayload } from './ResetPasswordModal';

export function TenantsPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const query = useQuery({ queryKey: ['operator', 'tenants'], queryFn: listTenants });

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm] = Form.useForm<CreateTenantRequest>();

  // Single dialog re-used for both "tenant created" and "password reset" outcomes —
  // both surface a one-shot cleartext credential the operator must record.
  const [credentialsDialog, setCredentialsDialog] = useState<{
    open: boolean;
    title: string;
    intro?: string;
    credentials: AdminAccountCredentials | null;
  }>({ open: false, title: '', credentials: null });

  const [resetTarget, setResetTarget] = useState<TenantView | null>(null);
  const [resetOpen, setResetOpen] = useState(false);

  const [editTarget, setEditTarget] = useState<TenantView | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm] = Form.useForm<UpdateTenantRequest>();

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['operator', 'tenants'] });

  const createMutation = useMutation({
    mutationFn: (payload: CreateTenantRequest) =>
      createTenant({ slug: payload.slug.trim(), name: payload.name.trim() }),
    onSuccess: (result) => {
      toast.success('租户已创建');
      setCreateOpen(false);
      void invalidate();
      setCredentialsDialog({
        open: true,
        title: `租户「${result.tenant.name}」已创建`,
        intro:
          '系统为新租户生成了初始管理员账号与密码，请立即复制并通过线下渠道告知租户。关闭对话框后将无法再次查看此密码。',
        credentials: result.initialAdmin,
      });
    },
    onError: (err: Error) => toast.error(err instanceof ApiError ? err.message : '创建失败'),
  });

  const suspendMutation = useMutation({
    mutationFn: (id: number) => suspendTenant(id),
    onSuccess: () => {
      toast.success('租户已禁用');
      void invalidate();
    },
    onError: (err: Error) => toast.error(err instanceof ApiError ? err.message : '操作失败'),
  });

  const activateMutation = useMutation({
    mutationFn: (id: number) => activateTenant(id),
    onSuccess: () => {
      toast.success('租户已启用');
      void invalidate();
    },
    onError: (err: Error) => toast.error(err instanceof ApiError ? err.message : '操作失败'),
  });

  const updateMutation = useMutation({
    mutationFn: (vars: { id: number; payload: UpdateTenantRequest }) =>
      updateTenant(vars.id, vars.payload),
    onSuccess: () => {
      toast.success('租户信息已更新');
      setEditOpen(false);
      setEditTarget(null);
      void invalidate();
    },
    onError: (err: Error) => toast.error(err instanceof ApiError ? err.message : '保存失败'),
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (vars: { id: number; payload: ResetPasswordSubmitPayload }) =>
      resetAdminPassword(vars.id, vars.payload),
    onSuccess: (creds) => {
      setResetOpen(false);
      const tenant = resetTarget;
      setResetTarget(null);
      setCredentialsDialog({
        open: true,
        title: tenant ? `「${tenant.name}」密码已重置` : '密码已重置',
        intro: '原密码已失效。请立即复制新密码并通过线下渠道告知租户。关闭对话框后将无法再次查看。',
        credentials: creds,
      });
    },
    onError: (err: Error) => toast.error(err instanceof ApiError ? err.message : '重置失败'),
  });

  const columns: TableProps<TenantView>['columns'] = [
    { title: 'ID', dataIndex: 'id', width: 80, fixed: 'left' },
    {
      title: '租户',
      width: 280,
      render: (_, record) => (
        <div>
          <div className="mall-row-title">{record.name}</div>
          <div className="mall-row-subtitle">
            <code className="mall-row-code">{record.slug}</code>
          </div>
        </div>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (status: TenantStatus) => (
        <Tag color={TENANT_STATUS_COLOR[status] ?? 'default'}>
          {TENANT_STATUS_LABEL[status] ?? status}
        </Tag>
      ),
    },
    {
      title: '操作',
      width: 360,
      fixed: 'right',
      render: (_, record) => (
        <Space wrap>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setEditTarget(record);
              editForm.setFieldsValue({ name: record.name, slug: record.slug });
              setEditOpen(true);
            }}
          >
            编辑信息
          </Button>
          {record.status === 'ACTIVE' ? (
            <Popconfirm
              title={`禁用租户「${record.name}」？`}
              description="禁用后该租户的管理后台、店铺前台、开放 API 都将无法访问。"
              okText="禁用"
              okButtonProps={{ danger: true }}
              onConfirm={() => suspendMutation.mutate(record.id)}
            >
              <Button size="small" icon={<PauseCircleOutlined />} danger>
                禁用
              </Button>
            </Popconfirm>
          ) : (
            <Popconfirm
              title={`启用租户「${record.name}」？`}
              okText="启用"
              onConfirm={() => activateMutation.mutate(record.id)}
            >
              <Button size="small" icon={<PlayCircleOutlined />} type="primary">
                启用
              </Button>
            </Popconfirm>
          )}
          <Tooltip title={record.status !== 'ACTIVE' ? '请先启用租户' : '重置该租户管理员密码'}>
            <Button
              size="small"
              icon={<KeyOutlined />}
              disabled={record.status !== 'ACTIVE'}
              onClick={() => {
                setResetTarget(record);
                setResetOpen(true);
              }}
            >
              重置密码
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="mall-page">
      <div className="mall-page-header">
        <div>
          <div className="mall-page-kicker">Tenants</div>
          <Typography.Title level={3} className="mall-page-title">
            租户管理
          </Typography.Title>
          <p className="mall-page-description">创建商户、管理启停状态，并处理管理员密码重置。</p>
        </div>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            loading={query.isFetching}
            onClick={() => query.refetch()}
          >
            刷新
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              createForm.resetFields();
              setCreateOpen(true);
            }}
          >
            新建租户
          </Button>
        </Space>
      </div>

      <div className="mall-table-card">
        <div className="mall-table-toolbar">
          <div>
            <div className="mall-table-title">租户列表</div>
            <div className="mall-table-subtitle">共 {query.data?.length ?? 0} 个租户</div>
          </div>
        </div>
        <ResponsiveList<TenantView>
          rowKey={(record) => record.id}
          loading={query.isLoading}
          dataSource={query.data ?? []}
          emptyText="暂无租户"
          pagination={{
            pageSize: 20,
            showTotal: (total, range) => `${range[0]}-${range[1]} / 共 ${total} 条`,
          }}
          tableProps={{
            size: 'small',
            columns,
            scroll: { x: 920 },
            locale: { emptyText: '暂无租户' },
          }}
          renderCard={(record) => (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ minWidth: 0 }}>
                  <div className="mall-row-title">{record.name}</div>
                  <div className="mall-row-subtitle">
                    #{record.id} · <code className="mall-row-code">{record.slug}</code>
                  </div>
                </div>
                <Tag color={TENANT_STATUS_COLOR[record.status] ?? 'default'}>
                  {TENANT_STATUS_LABEL[record.status] ?? record.status}
                </Tag>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => {
                    setEditTarget(record);
                    editForm.setFieldsValue({ name: record.name, slug: record.slug });
                    setEditOpen(true);
                  }}
                >
                  编辑信息
                </Button>
                {record.status === 'ACTIVE' ? (
                  <Popconfirm
                    title={`禁用租户「${record.name}」？`}
                    description="禁用后该租户的管理后台、店铺前台、开放 API 都将无法访问。"
                    okText="禁用"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => suspendMutation.mutate(record.id)}
                  >
                    <Button size="small" icon={<PauseCircleOutlined />} danger>
                      禁用
                    </Button>
                  </Popconfirm>
                ) : (
                  <Popconfirm
                    title={`启用租户「${record.name}」？`}
                    okText="启用"
                    onConfirm={() => activateMutation.mutate(record.id)}
                  >
                    <Button size="small" icon={<PlayCircleOutlined />} type="primary">
                      启用
                    </Button>
                  </Popconfirm>
                )}
                <Button
                  size="small"
                  icon={<KeyOutlined />}
                  disabled={record.status !== 'ACTIVE'}
                  onClick={() => {
                    setResetTarget(record);
                    setResetOpen(true);
                  }}
                >
                  重置密码
                </Button>
              </div>
            </div>
          )}
        />
      </div>
      {query.isError && (
        <Typography.Paragraph type="danger" style={{ marginTop: 16 }}>
          加载失败：{query.error instanceof Error ? query.error.message : '未知错误'}
        </Typography.Paragraph>
      )}

      <Modal
        title={editTarget ? `编辑租户 #${editTarget.id}` : '编辑租户'}
        open={editOpen}
        onOk={() => editForm.submit()}
        onCancel={() => {
          setEditOpen(false);
          setEditTarget(null);
        }}
        confirmLoading={updateMutation.isPending}
        destroyOnClose
      >
        <Typography.Paragraph type="secondary">
          可修改租户名称与子域名前缀。修改后，该租户管理员登录名将同步为新的子域名前缀，请提示租户使用新地址与账号登录。
        </Typography.Paragraph>
        <Form<UpdateTenantRequest>
          form={editForm}
          layout="vertical"
          onFinish={(v) => {
            if (!editTarget) return;
            updateMutation.mutate({
              id: editTarget.id,
              payload: { name: v.name!.trim(), slug: v.slug!.trim() },
            });
          }}
        >
          <Form.Item
            label="租户名称"
            name="name"
            rules={[{ required: true, message: '请填写租户名称' }]}
          >
            <Input maxLength={128} showCount />
          </Form.Item>
          <Form.Item
            label="子域名前缀（同时是管理员登录名）"
            name="slug"
            rules={[
              { required: true, message: '请填写子域名前缀' },
              {
                pattern: /^[a-z0-9](?:[a-z0-9-]{0,30}[a-z0-9])?$/,
                message: '小写字母/数字/中划线，长度 1~32，首尾不能为中划线',
              },
            ]}
          >
            <Input maxLength={32} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="新建租户"
        open={createOpen}
        onOk={() => createForm.submit()}
        onCancel={() => setCreateOpen(false)}
        confirmLoading={createMutation.isPending}
        destroyOnClose
      >
        <Typography.Paragraph type="secondary">
          创建后系统将为该租户自动生成一个初始管理员账号（账号 = slug），并在下一步显示一次性密码。
        </Typography.Paragraph>
        <Form<CreateTenantRequest>
          form={createForm}
          layout="vertical"
          onFinish={(v) => createMutation.mutate(v)}
        >
          <Form.Item
            label="子域名前缀（同时是登录账号）"
            name="slug"
            rules={[
              { required: true, message: '请填写子域名前缀' },
              {
                pattern: /^[a-z0-9](?:[a-z0-9-]{0,30}[a-z0-9])?$/,
                message: '小写字母/数字/中划线，长度 1~32，首尾不能为中划线',
              },
            ]}
          >
            <Input placeholder="如 acme（即子域名前缀）" />
          </Form.Item>
          <Form.Item
            label="租户名称"
            name="name"
            rules={[{ required: true, message: '请填写租户名称' }]}
          >
            <Input maxLength={64} />
          </Form.Item>
        </Form>
      </Modal>

      <ResetPasswordModal
        tenant={resetTarget}
        open={resetOpen}
        submitting={resetPasswordMutation.isPending}
        onCancel={() => {
          setResetOpen(false);
          setResetTarget(null);
        }}
        onSubmit={(payload) => {
          if (resetTarget) {
            resetPasswordMutation.mutate({ id: resetTarget.id, payload });
          }
        }}
      />

      <CredentialsModal
        open={credentialsDialog.open}
        title={credentialsDialog.title}
        intro={credentialsDialog.intro}
        credentials={credentialsDialog.credentials}
        onClose={() =>
          setCredentialsDialog({ open: false, title: '', credentials: null, intro: undefined })
        }
      />
    </div>
  );
}
